<template>
  <div
    v-if="showShippingRatesEnabled"
    class=""
  >
    <div class="accordion-item border-b">
      <h3>
        <button
          :aria-expanded="showShippingRates ? 'true' : 'false'"
          aria-controls="shipping-rates-panel"
          id="shipping-rates-header"
          class="flex items-center justify-between w-full py-4 transition-all duration-150 cursor-pointer"
          @click="handleShippingRatesToggle"
          @keydown="handleKeyDown"
        >
          <div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left flex-wrap">
            <span class="flex gap-2 relative items-center">
              <span
                v-if="showAccordionIcons"
                class="mini-cart__util-icon"
              >
                <MiniCartIconShipping />
              </span>
              {{ shippingRatesHeading }}
            </span>
            <div
              v-if="shippingRange"
              class="mini-cart__truncated-preview-text max-w-full!"
            >
              <span>
                {{ formatMoney(shippingRange.min) }}
                <template v-if="shippingRange.isRange">
                  – {{ formatMoney(shippingRange.max) }}
                  <span>{{ shippingOptionsLabel }}</span>
                </template>
              </span>
            </div>
          </div>
          <svg
            :class="showShippingRates ? 'rotate-180' : 'rotate-0'"
            class="w-4 h-4 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </button>
      </h3>
      <button
        v-if="showShippingRates"
        @click="handleShippingRatesToggle"
        class="w-full h-full fixed inset-0 mini-cart__blocker"
        style="
          background-color: var(--global-mask-color);
          backdrop-filter: var(--global-mask-blur);
        "
      >
        <span class="sr-only">close</span>
      </button>
      <div
        id="shipping-rates-panel"
        aria-labelledby="shipping-rates-header"
        class="mini-cart__slide-up-panel overflow-hidden transition-all duration-300 ease-in-out"
        :class="showShippingRates ? 'max-h-[80vh] opacity-100 panel_active' : 'panel_disabled'"
        @keydown="handlePanelKeyDown"
      >
      <div class="max-h-[80vh] overflow-y-auto">
        <div class="flex justify-between items-center">
          <span class="text-xs">{{ shippingRatesHeading }}</span>
          <button
            :aria-expanded="showShippingRates ? 'true' : 'false'"
            aria-controls="shipping-rates-panel"
            class="
            w-12 h-12 right-0 flex items-center justify-center
            transition-all duration-150
            cursor-pointer
          "
            @click="handleShippingRatesToggle"
            @keydown="handleKeyDown"
          >
            <MiniCartIconClose />
            <span class="sr-only">close</span>
          </button>
        </div>
        <div
          class="relative transition-all duration-300 ease-in-out"
          :class="showShippingRates ? 'opacity-100' : 'opacity-0'"
        >
          <!-- Shipping Rate Form -->
          <div class="mb-4 space-y-3">
            <div>
              <label
                for="shipping-country"
                class="block text-xs font-medium mb-1"
              >
                {{ countryLabel }}
              </label>
              <select
                id="shipping-country"
                v-model="formCountry"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option
                  v-for="country in availableCountries"
                  :key="country.iso_code"
                  :value="country.iso_code"
                >
                  {{ country.name }}
                </option>
              </select>
            </div>

            <div v-if="hasStates">
              <label
                for="shipping-province"
                class="block text-xs font-medium mb-1"
              >
                {{ provinceLabel }}
              </label>
              <select
                id="shipping-province"
                v-model="formProvince"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Select {{ provinceLabel }}</option>
                <option
                  v-for="state in availableStates"
                  :key="state.code"
                  :value="state.code"
                >
                  {{ state.name }}
                </option>
              </select>
            </div>

            <div v-else>
              <label
                for="shipping-province"
                class="block text-xs font-medium mb-1"
              >
                {{ provinceLabel }} (Optional)
              </label>
              <input
                id="shipping-province"
                v-model="formProvince"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="e.g., Region"
              />
            </div>

            <div>
              <label
                for="shipping-zip"
                class="block text-xs font-medium mb-1"
              >
                {{ zipLabel }}
              </label>
              <input
                id="shipping-zip"
                v-model="formZip"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="e.g., 90001"
              />
            </div>
          </div>

          <!-- Shipping Rates Results -->
          <ul
            v-if="shippingRates.length"
            class="mb-4"
          >
            <li
              v-for="(rate, index) in shippingRates"
              :key="index"
              class="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0 text-xs"
            >
              <span>{{ rate?.name }}</span>
              <span class="font-medium">{{ formatMoney(rate.price) }}</span>
            </li>
          </ul>

          <button
            class="mini-cart__button w-full mb-4"
            @click="fetchShippingRates({
              country: formCountry,
              province: formProvince,
              zip: formZip
            })"
          >
            {{ shippingRates.length ? shippingRatesUpdateButton : shippingRatesEstimateButton }}
          </button>

        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import formatMoney from "../helpers/format-money.js";
import { useAccordion } from './composables/useAccordion';
import MiniCartIconClose from './mini-cart--icon-close.vue';
import MiniCartIconShipping from './mini-cart--icon-shipping.vue';

// Cache for country/province data from external API
let countriesDataCache = null;
const showShippingRatesEnabled = computed(() => window.miniCartCustomSettings.showShippingRatesEnabled);
const shippingRatesHeading = computed(() => window.miniCartCustomSettings.shippingRatesHeading || 'Shipping rates');
const shippingRatesUpdateButton = computed(() => window.miniCartCustomSettings.shippingRatesUpdateButton || 'Update shipping estimate');
const shippingRatesEstimateButton = computed(() => window.miniCartCustomSettings.shippingRatesEstimateButton || 'Estimate shipping rates');
const shippingOptionsLabelTemplate = computed(() => window.miniCartCustomSettings.shippingOptionsLabelTemplate || '({count} options)');
const countryLabel = computed(() => window.miniCartCustomSettings.countryLabel || 'Country');
const provinceLabel = computed(() => window.miniCartCustomSettings.provinceLabel || 'State/Province');
const zipLabel = computed(() => window.miniCartCustomSettings.zipLabel || 'ZIP/Postal Code');
const showAccordionIcons = ref(window.miniCartCustomSettings?.showAccordionIcons ?? true);

// Fallback country list if Shopify Markets data is unavailable
const defaultCountries = [
  { name: 'United States', iso_code: 'US' },
  { name: 'Canada', iso_code: 'CA' },
  { name: 'United Kingdom', iso_code: 'GB' },
  { name: 'Australia', iso_code: 'AU' }
];

// Get available countries from Shopify markets
const availableCountries = computed(() => {
  const countries = window.miniCartCustomSettings?.availableCountries;

  // Use Shopify Markets data if available, otherwise fallback to default list
  const countryList = (countries && countries.length > 0) ? countries : defaultCountries;

  // Sort alphabetically by name
  return countryList.sort((a, b) => a.name.localeCompare(b.name));
});

// Get states/provinces for selected country
const availableStates = ref([]);
const loadingStates = ref(false);

/**
 * Load country/province data from Countries Now API (cached after first load)
 * @returns {Promise<Array>} Array of countries with their states
 */
async function loadCountriesData() {
  if (countriesDataCache) return countriesDataCache;

  try {
    const endpoint = window.miniCartCustomSettings?.countriesApiEndpoint;
    if (!endpoint) {
      console.error('Countries API endpoint not configured');
      return [];
    }

    const response = await fetch(endpoint);
    if (!response.ok) throw new Error('Failed to fetch countries data');

    const data = await response.json();

    // API returns: { error: false, msg: "...", data: [...] }
    if (data.error === false && data.data) {
      countriesDataCache = data.data;
      return countriesDataCache;
    }

    return [];
  } catch (error) {
    console.error('Error loading countries data:', error);
    return [];
  }
}

/**
 * Get provinces/states for a country from external API
 * @param {string} countryCode - ISO country code (e.g., 'US', 'CA')
 * @returns {Promise<Array>} Array of province objects with code and name
 */
async function getProvincesForCountry(countryCode) {
  if (!countryCode) return [];

  loadingStates.value = true;

  try {
    const countriesData = await loadCountriesData();
    if (!countriesData || countriesData.length === 0) return [];

    // Find country by ISO code (API uses full country names, so we need to match by ISO code)
    // First, get the full country name from our available countries
    const selectedCountry = availableCountries.value.find(c => c.iso_code === countryCode);
    if (!selectedCountry) return [];

    // Find country in API data by name
    const country = countriesData.find(c =>
      c.iso2 === countryCode ||
      c.iso3 === countryCode ||
      c.name.toLowerCase() === selectedCountry.name.toLowerCase()
    );

    if (!country || !country.states || country.states.length === 0) return [];

    // Transform states into our format
    return country.states.map(state => ({
      code: state.state_code || state.name,
      name: state.name
    }));
  } catch (error) {
    console.error('Error getting provinces:', error);
    return [];
  } finally {
    loadingStates.value = false;
  }
}

// Check if country has states
const hasStates = computed(() => availableStates.value.length > 0);
const shippingRange = computed(() => {
  if (!shippingRates.value.length) return null;
  const prices = shippingRates.value.map(rate => Math.round(parseFloat(rate.price) * 100));

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const isRange = prices.length > 1 && min !== max;

  return {
    min,
    max,
    isRange,
    count: prices.length,
  };
});

// Generate the shipping options label with count
const shippingOptionsLabel = computed(() => {
  if (!shippingRange.value) return '';
  return shippingOptionsLabelTemplate.value.replace(/{count}/g, shippingRange.value.count);
});

const searchPerformed = ref(false)
const shippingRates = ref([])
const savedAddress = ref(null)

// Form fields
const formCountry = ref('')
const formProvince = ref('')
const formZip = ref('')

// Watch for country changes and update available states
watch(() => formCountry.value, async (newCountry) => {
  formProvince.value = ''; // Reset province when country changes

  if (!newCountry) {
    availableStates.value = [];
    return;
  }

  // Get provinces from Shopify's country data
  availableStates.value = await getProvincesForCountry(newCountry);
});

// Accordion state
// const showShippingRates = ref(false);
const { isOpen: showShippingRates, toggle: toggleShippingRates, close: closeShippingRates } = useAccordion('shipping-rates');

// Get unique shop identifier for localStorage
const shopDomain = window.miniCartCustomSettings?.shopDomain || 'default';
// Extract store name from domain (e.g., "verthouseshop.myshopify.com" => "verthouseshop")
const shopIdentifier = shopDomain.split('.')[0];

// localStorage keys with shop-specific prefix
const STORAGE_KEY_RATES = `${shopIdentifier}_shopify_shipping_rates`;
const STORAGE_KEY_ADDRESS = `${shopIdentifier}_shopify_shipping_address`;

// Accordion functions
const handleShippingRatesToggle = () => {
  toggleShippingRates();

  // Toggle the mini-cart blocked state when shipping rates accordion is toggled
  if (window.setMiniCartBlocked) {
    window.setMiniCartBlocked(showShippingRates.value);
  }
};

// Handle keyboard events for accordion header
const handleKeyDown = (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleShippingRatesToggle();
  }
};

// Handle Escape key in accordion panel
const handlePanelKeyDown = (event) => {
  if (event.key === 'Escape') {
    closeShippingRates();

    // Unblock the mini-cart when closing via Escape
    if (window.setMiniCartBlocked) {
      window.setMiniCartBlocked(false);
    }

    // Return focus to header button
    const headerButton = document.getElementById('shipping-rates-header');
    if (headerButton) {
      headerButton.focus();
    }
  }
};

const fetchShippingRates = async (address) => {
  // Validate required fields
  if (!address.country || !address.zip) {
    alert('Please fill in Country and ZIP/Postal Code');
    return;
  }

  const shippingAddress = {
    country: address.country,
    province: address.province || '',
    zip: address.zip
  };

  try {
    const response = await fetch('/cart/shipping_rates.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shipping_address: shippingAddress
      })
    });

    const data = await response.json();
    console.log('Shipping rates response:', JSON.stringify(data))

    if (data.shipping_rates) {
      shippingRates.value = data.shipping_rates;
      savedAddress.value = shippingAddress;

      // Save to localStorage
      saveToLocalStorage(shippingAddress, data.shipping_rates);

      console.log('Shipping rates saved:', JSON.stringify(shippingRates.value))
    } else {
      alert('No shipping rates available.');
    }
  } catch (error) {
    console.error('Error fetching shipping rates:', error);
  }
};

/**
 * Save shipping rates and address to localStorage
 */
const saveToLocalStorage = (address, rates) => {
  try {
    const ratesData = {
      rates: rates,
      timestamp: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // Expires in 24 hours
    };

    const addressData = {
      address: address,
      timestamp: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY_RATES, JSON.stringify(ratesData));
    localStorage.setItem(STORAGE_KEY_ADDRESS, JSON.stringify(addressData));

    console.log('Saved to localStorage:', { address, rates });
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Load shipping rates and address from localStorage
 */
const loadFromLocalStorage = () => {
  try {
    const ratesData = localStorage.getItem(STORAGE_KEY_RATES);
    const addressData = localStorage.getItem(STORAGE_KEY_ADDRESS);

    if (ratesData && addressData) {
      const parsedRates = JSON.parse(ratesData);
      const parsedAddress = JSON.parse(addressData);

      // Check if data has expired
      const now = new Date();
      const expiresAt = new Date(parsedRates.expiresAt);

      if (expiresAt > now) {
        shippingRates.value = parsedRates.rates;
        savedAddress.value = parsedAddress.address;

        // Populate form fields with saved address
        if (parsedAddress.address) {
          formCountry.value = parsedAddress.address.country || 'US';
          formProvince.value = parsedAddress.address.province || '';
          formZip.value = parsedAddress.address.zip || '';
        }

        console.log('Loaded from localStorage:', { address: parsedAddress.address, rates: parsedRates.rates });
        return true;
      } else {
        // Data expired, clear it
        clearLocalStorage();
        console.log('Cached shipping rates expired');
      }
    }
  } catch (error) {
    console.error('Error loading from localStorage:', error);
  }
  return false;
};

/**
 * Clear shipping rates from localStorage
 */
const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY_RATES);
    localStorage.removeItem(STORAGE_KEY_ADDRESS);
    console.log('Cleared shipping rates from localStorage');
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

// Load saved data on mount
onMounted(async () => {
  const hasLoadedData = loadFromLocalStorage();
  const savedProvince = formProvince.value;
  if (availableCountries.value.length > 0 && !formCountry.value) {
    formCountry.value = availableCountries.value[0].iso_code;
  }
  if (formCountry.value) {
    availableStates.value = await getProvincesForCountry(formCountry.value);
    if (savedProvince && hasLoadedData) {
      formProvince.value = savedProvince;
    }
  }
});
</script>